import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { useT } from "@/content/landing";
import { BrandLogo } from "@/components/BrandLogo";
import { LanguageSwitcher } from "@/components/landing/LanguageSwitcher";
import { Footer } from "@/components/landing/Footer";
import { submitQuoteRequest } from "@/lib/servicesApi";
import { EMPTY_QUOTE, QUOTE_ITEMS, buildQuotePayload, toggleItem, validateQuote } from "@/lib/quote";

const inputClass = "mt-1.5 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-[#9AA7B2] focus-visible:border-[#3B82F6] focus-visible:ring-2 focus-visible:ring-[#3B82F6]/30 focus-visible:outline-none";
const errCls = "mt-1.5 text-xs text-[#EF4444]";

export default function Quote() {
  const t = useT();
  const q = t.quote;
  const [params] = useSearchParams();
  const reportToken = params.get("report");
  const [values, setValues] = useState(EMPTY_QUOTE);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = q.metaTitle;
  }, [q.metaTitle]);

  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    setErrors((p) => ({ ...p, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const next = validateQuote(values, q.errors);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSending(true);
    setFailed(false);
    try {
      await submitQuoteRequest(buildQuotePayload(values, reportToken));
      setDone(true);
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };

  const field = (key, label, extra = {}) => (
    <div className={extra.wide ? "sm:col-span-2" : undefined}>
      <label htmlFor={`q-${key}`} className="text-sm font-medium text-[#475569]">
        {label}
        {extra.optional && <span className="text-[#94A3B8]"> ({q.optional})</span>}
        {!extra.optional && " *"}
      </label>
      <input id={`q-${key}`} name={key} type={extra.type || "text"} value={values[key]} onChange={set(key)} maxLength={extra.max} aria-invalid={!!errors[key]} aria-describedby={errors[key] ? `qerr-${key}` : undefined} className={inputClass} />
      {errors[key] && <p id={`qerr-${key}`} className={errCls}>{errors[key]}</p>}
    </div>
  );

  return <div className="min-h-screen bg-[#FAFBFC] font-sans antialiased">
      <header className="border-b border-black/5 bg-white">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between gap-3 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5"><BrandLogo className="h-11" /></Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/" className="hidden items-center gap-1.5 text-sm font-medium text-[#64748B] hover:text-[#0F172A] sm:inline-flex">
              <ArrowLeft size={15} aria-hidden /> {t.ui.backToHome}
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
        <h1 className="text-3xl font-bold text-[#0F172A]">{q.title}</h1>
        <p className="mt-4 text-base leading-relaxed text-[#64748B]">{q.lead}</p>

        {done ? <div className="mt-10 rounded-3xl border border-[rgba(107,213,78,0.4)] bg-white p-8 text-center" role="status">
            <CheckCircle2 size={34} aria-hidden className="mx-auto" style={{ color: "#22C55E" }} />
            <h2 className="mt-4 text-lg font-semibold text-[#0F172A]">{q.successTitle}</h2>
            <p className="mt-2 text-sm leading-relaxed text-[#64748B]">{q.successBody}</p>
          </div> : <form onSubmit={onSubmit} noValidate className="mt-10 rounded-3xl border border-black/5 bg-white p-6 sm:p-8">
            <div className="grid gap-5 sm:grid-cols-2">
              {field("name", q.fields.name, { max: 200 })}
              {field("company", q.fields.company, { max: 200 })}
              {field("email", q.fields.email, { type: "email", max: 200 })}
              {field("phone", q.fields.phone, { optional: true, max: 50 })}

              <fieldset className="sm:col-span-2">
                <legend className="text-sm font-medium text-[#475569]">{q.fields.scope} *</legend>
                <div className="mt-2 flex flex-col gap-2 text-sm text-[#0F172A]">
                  {[["full", q.scopeFull], ["items", q.scopeItems]].map(([val, label]) => <label key={val} className="flex items-center gap-2.5">
                      <input type="radio" name="scope" value={val} checked={values.scope === val} onChange={() => {
                    setValues((v) => ({ ...v, scope: val }));
                    setErrors((p) => ({ ...p, items: undefined }));
                  }} className="h-4 w-4 accent-[#3B82F6]" />
                      {label}
                    </label>)}
                </div>
              </fieldset>

              {values.scope === "items" && <fieldset className="sm:col-span-2" data-testid="quote-items">
                  <legend className="text-sm font-medium text-[#475569]">{q.fields.items} *</legend>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {QUOTE_ITEMS.map((code) => <label key={code} className="flex items-center gap-2.5 text-sm text-[#0F172A]">
                        <input type="checkbox" checked={values.items.includes(code)} onChange={() => {
                      setValues((v) => ({ ...v, items: toggleItem(v.items, code) }));
                      setErrors((p) => ({ ...p, items: undefined }));
                    }} className="h-4 w-4 accent-[#3B82F6]" />
                        {q.itemLabels[code]}
                      </label>)}
                  </div>
                  {errors.items && <p className={errCls}>{errors.items}</p>}
                </fieldset>}

              <div className="sm:col-span-2">
                <label htmlFor="q-message" className="text-sm font-medium text-[#475569]">
                  {q.fields.message} <span className="text-[#94A3B8]">({q.optional})</span>
                </label>
                <textarea id="q-message" name="message" rows={5} maxLength={4000} value={values.message} onChange={set("message")} className={inputClass} />
              </div>
            </div>

            <div className="mt-6 flex items-start gap-3">
              <input id="q-consent" type="checkbox" checked={values.consent} onChange={(e) => {
            setValues((v) => ({ ...v, consent: e.target.checked }));
            setErrors((p) => ({ ...p, consent: undefined }));
          }} aria-invalid={!!errors.consent} aria-describedby={errors.consent ? "qerr-consent" : undefined} className="mt-1 h-4 w-4 shrink-0 accent-[#3B82F6]" />
              <label htmlFor="q-consent" className="text-sm leading-relaxed text-[#64748B]">
                {q.consent}{" "}
                <Link to="/datenschutz" className="font-medium text-[#3B82F6] underline">{q.consentLinkLabel}</Link>
              </label>
            </div>
            {errors.consent && <p id="qerr-consent" className={errCls}>{errors.consent}</p>}

            {failed && <p role="alert" className="mt-5 rounded-xl bg-[#FEF2F2] px-4 py-3 text-sm text-[#B91C1C]">{q.failure}</p>}

            <button type="submit" disabled={sending} className="mt-7 w-full rounded-full bg-[#0F172A] px-6 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2 focus-visible:outline-none disabled:opacity-70 motion-reduce:hover:translate-y-0 sm:w-auto">
              {sending ? q.sending : q.submit}
            </button>
          </form>}
      </main>
      <Footer />
    </div>;
}
